import React, { Dispatch, FC, SetStateAction } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { WidgetsSetup } from 'components/posts/detail/layout/widgetsSetup/WidgetsSetupProvider';
import WidgetsSetupModalReorderableListItem from 'components/posts/detail/layout/widgetsSetup/widgetsSetupModal/WidgetsSetupModalReorderableListItem';
import { useTranslation } from 'react-i18next';
import { Divider } from '@kontentino/ui';
import {
  organizeWidgetsSetup,
  segmentWidgets,
} from 'components/posts/detail/layout/widgetsSetup/widgetsSetupModal/utils/reordeableList';

type Props = {
  value: WidgetsSetup[];
  onChange: Dispatch<SetStateAction<WidgetsSetup[]>>;
  portalRoot: HTMLElement | null;
};

const WidgetsSetupModalReordeableList: FC<Props> = ({
  value,
  portalRoot,
  onChange,
}) => {
  const { t } = useTranslation();
  const { expanded, collapsed } = segmentWidgets(value);

  function onDragEnd(result: DropResult) {
    onChange((prevValue) => organizeWidgetsSetup(prevValue, result));
  }

  function renderDroppable({
    id,
    value,
  }: {
    id: string;
    value: WidgetsSetup[];
  }) {
    return (
      <Droppable droppableId={id} direction="vertical">
        {(droppableProvided, snapshot) => (
          <div
            {...droppableProvided.droppableProps}
            ref={droppableProvided.innerRef}
            className="tw-mb-4 tw-flex tw-flex-col tw-gap-4 tw-overflow-hidden"
          >
            {value.map((widget, index) => {
              return (
                <Draggable
                  key={widget.key}
                  draggableId={widget.key}
                  index={index}
                >
                  {(draggableProvided, snapshot) => (
                    <WidgetsSetupModalReorderableListItem
                      provided={draggableProvided}
                      snapshot={snapshot}
                      key={widget.key}
                      portal={portalRoot}
                      widget={widget}
                    />
                  )}
                </Draggable>
              );
            })}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="tw-mb-4 tw-flex tw-flex-col tw-space-y-4">
        <Divider />
        <span className="tw-text-sm tw-font-medium tw-text-grayscale-100">
          {t('expanded')}
        </span>
      </div>
      {renderDroppable({
        id: 'expanded',
        value: expanded,
      })}
      <div className="tw-mb-4 tw-flex tw-flex-col tw-space-y-4">
        <Divider />
        <span className="tw-text-sm tw-font-medium tw-text-grayscale-100">
          {t('collapsed')}
        </span>
      </div>
      {renderDroppable({
        id: 'collapsed',
        value: collapsed,
      })}
    </DragDropContext>
  );
};

export default WidgetsSetupModalReordeableList;
